import {H1, H2} from "../Headlines";
import {
    CalendarDateRangeIcon,
    ChatBubbleLeftRightIcon,
    DocumentTextIcon,
    FireIcon,
    HomeIcon,
    IdentificationIcon,
    PresentationChartLineIcon,
    UsersIcon,
    WrenchScrewdriverIcon
} from "@heroicons/react/24/solid";
import Paragraph from "../Paragraph";
import Section from "./Section";
import Energieausweis from "./Energieausweis";
import Foerderberatung from "./Foerderberatung";
import Heizlastberechnung from "./Heizlastberechnung";
import ISFP from "./ISFP";
import EnergieberatungHauskauf from "./EnergieberatungHauskauf";
import NachweisAnteilErneuerbareEnergien from "./NachweisAnteilErneuerbareEnergien";
import EnergetischeBaubegleitung from "./EnergetischeBaubegleitung";
import React, {useState} from "react";
import EnergetischeErstberatung from "./EnergetischeErstberatung";
import Waermepumpencheck from "./Waermepumpencheck";
import Logo from "../Logo";
import {useMediaQuery} from "react-responsive";
import {NavLink} from "react-router-dom";

type Item = {
    href: string;
    label: string;
    Icon: any
}

export default function Leistungen({short = false}: { short?: boolean }) {
    const [active, setActive] = useState('energetische-erstberatung');

    const isMobile = useMediaQuery({maxWidth: 767});

    const items: Item[] = [
        {href: 'energieausweis', label: 'Energieausweis', Icon: IdentificationIcon},
        {href: 'waermepumpencheck', label: 'Wärmepumpencheck', Icon: PresentationChartLineIcon},
        {href: 'foerderberatung', label: 'Förderberatung', Icon: ChatBubbleLeftRightIcon},
        {href: 'energetische-baubegeleitung', label: 'Energetische Baubegleitung', Icon: WrenchScrewdriverIcon},
        {href: 'energetische-erstberatung', label: 'Energetische Erstberatung vor Ort', Icon: UsersIcon},
        {href: 'energieberatung-zum-hauskauf', label: 'Energieberatung zum Hauskauf', Icon: HomeIcon},
        {
            href: 'isfp-individueller-sanierungsfahrplan',
            label: 'Individueller Sanierungsfahrplan (ISFP)',
            Icon: CalendarDateRangeIcon
        },
        {href: 'heizlastberechnung', label: 'Heizlastberechnung', Icon: FireIcon},
        {
            href: 'nachweis-anteil-erneuerbarer-energien',
            label: 'Nachweis Anteil erneuerbarer Energien',
            Icon: DocumentTextIcon
        },
    ];

    function addCircleItems(item: any, posX: number, posY: number) {
        return (
            <li key={item.href}
                id={'circle-' + item.href}
                className={'group absolute cursor-pointer ' + (active === item.href ? 'pt-0 pl-0' : 'pt-2 pl-2 hover:pt-0 hover:pl-0')}
                style={{left: posX, top: posY}}>
                <button className="relative block"
                        onClick={() => setActive(item.href)}
                >
                    {active !== item.href && <span
                        className="flex items-center justify-center w-16 h-16 hover:h-20 hover:w-20 hover:shadow-2xl hover:shadow-green-600/30 rounded-full bg-white shadow ring-1 ring-green-600/20 text-green-600">
                                            <item.Icon className="p-4"/>
                                        </span>}
                    {active === item.href && <span
                        className="flex items-center justify-center h-20 w-20 shadow-2xl shadow-green-600/30 rounded-full bg-green-600 ring-1 ring-green-600/20 text-white">
                                            <item.Icon className="p-4"/>
                                        </span>}
                    <span
                        className="z-10 pointer-events-none absolute left-1/2 -translate-x-1/2 mt-2 whitespace-nowrap text-sm bg-white/95 text-gray-800 px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity shadow">
                                            {item.label}
                                        </span>
                </button>
            </li>
        );
    }

    return <>
        {short && circle(isMobile, items, addCircleItems, active)}
        {!short && singlePageAll()}
    </>
}

function circle(isMobile: boolean, items: Item[], addCircleItems: (items: Item[], x: number, y: number) => any, active: string) {
    return <>
        <div className={'devide-y divide-gray-200 '}>
            <Section id={'leistungen'} className={''}>
                <H2 id={'leistungen-headline'}>
                    Unsere Leistungen
                </H2>
                <Paragraph className={'mb-16 mt-8'}>Wählen Sie aus dem <strong>Auswahlrad</strong> eine Leistung aus
                    <br/>oder<br/>
                    springen Sie auf unsere <NavLink to={'/leistungen'} className={'underline hover:text-gray-900'}>komplette
                        Leistungsübersicht</NavLink>.
                </Paragraph>
                {!isMobile && <AuswahlringDesktop items={items} addCircleItems={addCircleItems}/>}
                {isMobile && <AuswahlringMobile items={items} addCircleItems={addCircleItems}/>}
            </Section>

            <EnergetischeErstberatung activeLeistung={active} inListing={false}/>
            <Waermepumpencheck activeLeistung={active} inListing={false}/>
            <Foerderberatung activeLeistung={active} inListing={false}/>
            <Energieausweis activeLeistung={active} inListing={false}/>
            <EnergetischeBaubegleitung activeLeistung={active} inListing={false}/>
            <EnergieberatungHauskauf activeLeistung={active} inListing={false}/>
            <ISFP activeLeistung={active} inListing={false}/>
            <Heizlastberechnung activeLeistung={active} inListing={false}/>
            <NachweisAnteilErneuerbareEnergien activeLeistung={active} inListing={false}/>
        </div>

    </>
}

function singlePageAll() {
    return <>
        <div className={'devide-y divide-gray-200 '}>
            <Section id={'leistungen'} className={'!pt-0'}>
                <H1 id={'leistungen-headline'} className={'mb-8'}>
                    Unsere Leistungen
                </H1>
                <div className={'lg:pl-48 text-left'}>
                    <Paragraph className={'mt-2'}><a className={'flex hover:text-gray-900'}
                                                     href={'#energetische-erstberatung'}><UsersIcon
                        className={'text-green-600 h-6 mr-2'}/>Energetische Erstberatung vor Ort</a></Paragraph>
                    <Paragraph className={'mt-2'}><a className={'flex hover:text-gray-900'} href={'#waermepumpencheck'}><PresentationChartLineIcon
                        className={'text-green-600 h-6 mr-2'}/>Wärmepumpencheck</a></Paragraph>
                    <Paragraph className={'mt-2'}><a className={'flex hover:text-gray-900'}
                                                     href={'#foerderberatung'}><ChatBubbleLeftRightIcon
                        className={'text-green-600 h-6 mr-2'}/>Förderberatung</a></Paragraph>
                    <Paragraph className={'mt-2'}><a className={'flex hover:text-gray-900'}
                                                     href={'#energieausweis'}><IdentificationIcon
                        className={'text-green-600 h-6 mr-2'}/>Energieausweis</a></Paragraph>
                    <Paragraph className={'mt-2'}><a className={'flex hover:text-gray-900'}
                                                     href={'#energetische-baubegeleitung'}><WrenchScrewdriverIcon
                        className={'text-green-600 h-6 mr-2'}/>Energetische Baubegleitung</a></Paragraph>
                    <Paragraph className={'mt-2'}><a className={'flex hover:text-gray-900'}
                                                     href={'#energieberatung-zum-hauskauf'}><HomeIcon
                        className={'text-green-600 h-6 mr-2'}/>Energieberatung zum Hauskauf</a></Paragraph>
                    <Paragraph className={'mt-2'}><a className={'flex hover:text-gray-900'}
                                                     href={'#isfp-individueller-sanierungsfahrplan'}><CalendarDateRangeIcon
                        className={'text-green-600 h-6 mr-2'}/>Individueller Sanierungsfahrplan (ISFP)</a></Paragraph>
                    <Paragraph className={'mt-2'}><a className={'flex hover:text-gray-900'}
                                                     href={'#heizlastberechnung'}><FireIcon
                        className={'text-green-600 h-6 mr-2'}/>Heizlastberechnung</a></Paragraph>
                    <Paragraph className={'mt-2'}><a className={'flex hover:text-gray-900'}
                                                     href={'#nachweis-anteil-erneuerbarer-energien'}><DocumentTextIcon
                        className={'text-green-600 h-6 mr-2'}/>Nachweis Anteil erneuerbarer Energien</a></Paragraph>
                </div>
            </Section>

            <EnergetischeErstberatung/>
            <Waermepumpencheck/>
            <Foerderberatung/>
            <Energieausweis/>
            <EnergetischeBaubegleitung/>
            <EnergieberatungHauskauf/>
            <ISFP/>
            <Heizlastberechnung/>
            <NachweisAnteilErneuerbareEnergien/>
        </div>

    </>
}

interface PropsAuswahlRing {
    items: Item[],
    addCircleItems: (item: any, x: number, y: number) => any,
}

function AuswahlringDesktop({items, addCircleItems}: PropsAuswahlRing) {
    return <>
        <div className="relative mx-auto" style={{width: '28rem', height: '28rem'}}>
            <div className="absolute inset-0 rounded-full"></div>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
                <Logo className="w-40"/>
                <p className={'text-xl sm:text-3xl pt-2 font-bold tracking-tight text-gray-900'}>Unsere Leistungen</p>
            </div>
            <ul className="relative w-full h-full list-none m-0 p-0">
                {items.map((item, idx) => {
                    const angle = (idx / items.length) * 360; // degrees
                    const radius = 200; // px from center
                    const x = 224 + radius * Math.cos((angle - 90) * Math.PI / 180) - 40; // center at 224px (28rem/2)
                    const y = 224 + radius * Math.sin((angle - 90) * Math.PI / 180) - 40;

                    return addCircleItems(item, x, y);
                })}
            </ul>
            <style>

            </style>
        </div>
    </>
}

function AuswahlringMobile({items, addCircleItems}: PropsAuswahlRing) {
    return <>
        <div className="relative mx-auto" style={{width: '20rem', height: '20rem'}}>
            <div className="absolute inset-0 rounded-full"></div>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
                <Logo className="w-40"/>
                <p className={'text-xl sm:text-3xl pt-2 font-bold tracking-tight text-gray-900'}>Unsere Leistungen</p>
            </div>
            <ul className="relative w-full h-full list-none m-0 p-0">
                {items.map((item, idx) => {
                    const angle = (idx / items.length) * 360; // degrees
                    const radius = 140; // px from center
                    const x = 160 + radius * Math.cos((angle - 90) * Math.PI / 180) - 40; // center at 224px (28rem/2)
                    const y = 160 + radius * Math.sin((angle - 90) * Math.PI / 180) - 40;

                    return addCircleItems(item, x, y);
                })}
            </ul>
            <style>

            </style>
        </div>
    </>
}

export function ZurLeistungsuebersicht() {
    return <>
        <Paragraph className={'mt-20 text-right text-md'}>
            <a href={'#leistungen'} className={'underline'}>{'>> Zur Leistungsübersicht'}</a>
        </Paragraph>
    </>
}